import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bell } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const dropdownRef = useRef(null);
  
  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications/unread-count`);
      setUnreadCount(res.data.count);
    } catch {}
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications`);
      setNotifications(res.data);
    } catch {}
  };

  const toggleNotifications = async () => {
    if (!showNotifications) await fetchNotifications();
    setShowNotifications(!showNotifications);
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/admin/notifications/read-all`);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {}
  };

  return (
    <nav className="fixed top-0 w-full z-50 glassmorphism" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          <Link 
            to="/" 
            className="text-2xl font-serif font-bold tracking-tight text-slate-900" 
            data-testid="logo-link"
          >
            The Case Verse
          </Link>
          <div className="flex items-center gap-6">
            <Link
              to="/search"
              className={`transition-colors ${isActive('/search') ? 'text-slate-900 font-medium' : 'text-slate-700 hover:text-slate-900'}`}
              data-testid="nav-case-library-link"
            >
              Case Library
            </Link>
            <Link 
              to="/videos" 
              className={`transition-colors ${isActive('/videos') ? 'text-slate-900 font-medium' : 'text-slate-700 hover:text-slate-900'}`}
              data-testid="nav-videos-link"
            >
              Videos
            </Link>
            <Link
              to="/simulations"
              className={`transition-colors ${isActive('/simulations') ? 'text-slate-900 font-medium' : 'text-slate-700 hover:text-slate-900'}`}
              data-testid="nav-simulations-link"
            >
              Simulations
            </Link>
            <Link 
              to="/research" 
              className={`transition-colors ${isActive('/research') ? 'text-slate-900 font-medium' : 'text-slate-700 hover:text-slate-900'}`}
              data-testid="nav-research-link"
            >
              Research
            </Link>
            <Link 
              to="/about" 
              className={`transition-colors ${isActive('/about') ? 'text-slate-900 font-medium' : 'text-slate-700 hover:text-slate-900'}`}
              data-testid="nav-about-link"
            >
              About
            </Link>
            <Link
              to="/subscribers"
              className={`transition-colors ${isActive('/subscribers') ? 'text-slate-900 font-medium' : 'text-slate-700 hover:text-slate-900'}`}
              data-testid="nav-subscribers-link"
            >
              Our Subscribers
            </Link>
            {user ? (
              <>
                <Link
                  to="/cases"
                  className={`transition-colors ${isActive('/cases') ? 'text-slate-900 font-medium' : 'text-slate-700 hover:text-slate-900'}`}
                  data-testid="nav-browse-cases-link"
                >
                  Browse Cases
                </Link>
                {user.role === 'admin' && (
                  <Link 
                    to="/admin" 
                    className={`transition-colors ${isActive('/admin') ? 'text-slate-900 font-medium' : 'text-slate-700 hover:text-slate-900'}`}
                    data-testid="nav-admin-link"
                  >
                    Admin
                  </Link>
                )}
                {user.role === 'admin' && (
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={toggleNotifications}
                      className="relative p-1.5 text-slate-600 hover:text-slate-900 transition-colors"
                      data-testid="notification-bell"
                    >
                      <Bell className="w-5 h-5" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </button>
                    {showNotifications && (
                      <div className="absolute right-0 top-10 w-96 bg-white rounded-xl border border-slate-200 shadow-xl z-[100] overflow-hidden" data-testid="notification-dropdown">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
                          <span className="font-semibold text-slate-900 text-sm">Notifications</span>
                          {unreadCount > 0 && (
                            <button onClick={markAllRead} className="text-xs text-sky-600 hover:text-sky-800 font-medium" data-testid="mark-all-read-btn">
                              Mark all read
                            </button>
                          )}
                        </div>
                        <div className="max-h-80 overflow-y-auto">
                          {notifications.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-6">No notifications yet</p>
                          ) : (
                            notifications.map(n => (
                              <div
                                key={n.id}
                                className={`px-4 py-3 border-b border-slate-50 last:border-0 ${!n.is_read ? 'bg-sky-50/50' : ''}`}
                                data-testid={`notification-item-${n.id}`}
                              >
                                <p className="text-sm text-slate-800 font-medium">{n.title}</p>
                                <p className="text-xs text-slate-600 mt-0.5">{n.message}</p>
                                <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {user.role === 'institution_admin' && (
                  <Link 
                    to="/institution" 
                    className={`transition-colors ${isActive('/institution') ? 'text-slate-900 font-medium' : 'text-slate-700 hover:text-slate-900'}`}
                    data-testid="nav-institution-link"
                  >
                    Institution
                  </Link>
                )}
                {(user.role === 'instructor' || user.role === 'institution_admin') && (
                  <Link 
                    to="/instructor" 
                    className={`transition-colors ${isActive('/instructor') ? 'text-slate-900 font-medium' : 'text-slate-700 hover:text-slate-900'}`}
                    data-testid="nav-instructor-link"
                  >
                    Instructor
                  </Link>
                )}
                <Link 
                  to="/book-training" 
                  className={`transition-colors ${isActive('/book-training') ? 'text-slate-900 font-medium' : 'text-slate-700 hover:text-slate-900'}`}
                  data-testid="nav-book-training-link"
                >
                  Book Training
                </Link>
                <Link 
                  to="/request-case" 
                  className={`transition-colors ${isActive('/request-case') ? 'text-slate-900 font-medium' : 'text-slate-700 hover:text-slate-900'}`}
                  data-testid="nav-request-case-link"
                >
                  Request Case
                </Link>
                <Link 
                  to="/dashboard" 
                  className={`transition-colors ${isActive('/dashboard') ? 'text-slate-900 font-medium' : 'text-slate-700 hover:text-slate-900'}`}
                  data-testid="nav-dashboard-link"
                >
                  Dashboard
                </Link>
                <button
                  onClick={logout}
                  className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800 transition-all"
                  data-testid="logout-button"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/book-training"
                  className={`transition-colors ${isActive('/book-training') ? 'text-slate-900 font-medium' : 'text-slate-700 hover:text-slate-900'}`}
                  data-testid="nav-book-training-link"
                >
                  Book Training
                </Link>
                <Link
                  to="/request-case"
                  className={`transition-colors ${isActive('/request-case') ? 'text-slate-900 font-medium' : 'text-slate-700 hover:text-slate-900'}`}
                  data-testid="nav-request-case-link"
                >
                  Request Case
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center px-5 py-2 bg-orange-600 text-white rounded-full hover:bg-orange-700 transition-all font-medium text-sm shadow-sm"
                  data-testid="nav-login-signup-button"
                >
                  Login / Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
